import { z } from "zod";

import { staffPasswordSchema } from "./login";
import { ONE_TARGET, oneTarget, otpCodeSchema, passwordSchema, phoneOrEmail } from "./signup";

/**
 * Changing, resetting and recovering a credential.
 *
 * Three routes in, because the three situations are genuinely different:
 *
 *   change    you know your current password — prove it, pick a new one
 *   reset     an admin sets a new one for somebody who forgot theirs
 *   recover   you know nothing — prove the phone or email instead
 *
 * Staff have no recovery route of their own, deliberately. Their password is
 * reset by the owner, who is the correct authority: a cashier recovering their
 * own account without the owner knowing is precisely what this product exists
 * to prevent. It is also why the owner can do it from their own phone, from
 * anywhere — the alternative is a cashier who cannot work until someone drives
 * to the shop.
 */

/* --------------------------------------------------------- change own */

/**
 * `newPassword` carries the STAFF floor of four characters, not the owner's
 * ten, because one route serves both roles. The stronger rule is applied by
 * role in `credentials.service.ts` — zod cannot see who is calling, and a
 * schema that assumed the weaker rule everywhere would quietly let an owner
 * downgrade the credential that reaches billing.
 */
export const changePasswordRequest = z.object({
  currentPassword: z.string().min(1, "Enter your current password"),
  newPassword: staffPasswordSchema,
});



/* ------------------------------------------------------ admin resets */

/**
 * An owner generates a new password for a staff member who has forgotten
 * theirs, or who has locked themselves out.
 */
export const resetStaffPasswordRequest = z
  .object({
    /** Omitted means generate one. Supplied means the owner chose it. */
    password: staffPasswordSchema.optional(),
  })
  /**
   * Tolerates no body at all, which is what "just generate one" looks like on
   * the wire. Without this the commonest call — an owner tapping Reset — is a
   * 400 because it sent nothing.
   */
  .nullish()
  .transform((v) => v ?? {});

export const resetStaffPasswordResponse = z.object({
  userId: z.string().uuid(),
  username: z.string(),
  /** Shown once, to hand over. The old password stops working at once. */
  password: z.string(),
});

/* ------------------------------------------------------- recover own */

/** Where a code was sent: shown back, partly hidden, so a typo is obvious. */
const codeSent = z.object({
  channel: z.enum(["phone", "email"]),
  sentTo: z.string(),
  /** For a phone: the same as `sentTo`. Kept for apps from before email. */
  maskedPhone: z.string().optional(),
  expiresInSeconds: z.number().int().positive(),
  /** Development only, and only when a code was actually sent. */
  devCode: z.string().optional(),
});

export const forgotPasswordRequest = z.object(phoneOrEmail).refine(oneTarget, ONE_TARGET);

/**
 * Deliberately says nothing about whether the number or email is registered.
 *
 * A different answer for a known and unknown one turns this into a way to
 * discover which pharmacy owners use the product — the same disclosure removed
 * from signup, and it would be pointless to close one and leave the other open.
 */
export const forgotPasswordResponse = codeSent;

/**
 * Check the code on its own, before a new password is asked for, so a wrong
 * one is caught on the code page rather than after somebody has typed a
 * password. The code is spent here; what comes back is a short-lived proof
 * that `reset-password` spends in its place.
 */
export const resetPasswordVerifyRequest = z
  .object({ ...phoneOrEmail, code: otpCodeSchema })
  .refine(oneTarget, ONE_TARGET);

export const resetPasswordVerifyResponse = z.object({
  resetToken: z.string(),
  expiresInSeconds: z.number().int().positive(),
});

/**
 * Either the proof from `reset-password/verify`, or — for apps from before
 * the code had its own page — the phone or email with the code itself.
 */
export const resetPasswordRequest = z
  .object({
    ...phoneOrEmail,
    code: otpCodeSchema.optional(),
    resetToken: z.string().min(1).optional(),
    newPassword: passwordSchema,
  })
  .refine(
    (v) => (v.resetToken ? !v.phone && !v.email && !v.code : oneTarget(v) && Boolean(v.code)),
    ONE_TARGET,
  );

/* --------------------------------------------------- invite an owner */

export const inviteAdminRequest = z
  .object({
    name: z.string().trim().min(2, "Enter their name").max(120),
    /**
     * The invitee's OWN number or email — exactly one — not the inviter's.
     *
     * It becomes their login and their recovery channel, so it has to be
     * theirs. One number or email is one account across the whole system, so
     * somebody who already owns a pharmacy needs a different one for a second.
     */
    ...phoneOrEmail,
  })
  .refine(oneTarget, ONE_TARGET);

export const inviteAdminResponse = codeSent.extend({ userId: z.string().uuid() });

export const acceptInviteRequest = z
  .object({ ...phoneOrEmail, code: otpCodeSchema, password: passwordSchema })
  .refine(oneTarget, ONE_TARGET);

/* --------------------------------------- add or change a way to sign in */

/**
 * An owner adds, or changes, the phone or email they sign in and recover
 * with — proven with a code sent to the NEW one, so nobody can attach an
 * address they do not hold.
 */
export const contactStartRequest = z.object(phoneOrEmail).refine(oneTarget, ONE_TARGET);
export const contactStartResponse = codeSent;

export const contactConfirmRequest = z
  .object({ ...phoneOrEmail, code: otpCodeSchema })
  .refine(oneTarget, ONE_TARGET);

export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;
export type ResetStaffPasswordRequest = z.infer<typeof resetStaffPasswordRequest>;
export type ResetStaffPasswordResponse = z.infer<typeof resetStaffPasswordResponse>;
export type ForgotPasswordResponse = z.infer<typeof forgotPasswordResponse>;
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequest>;
export type ResetPasswordVerifyRequest = z.infer<typeof resetPasswordVerifyRequest>;
export type ResetPasswordVerifyResponse = z.infer<typeof resetPasswordVerifyResponse>;
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequest>;
export type InviteAdminRequest = z.infer<typeof inviteAdminRequest>;
export type InviteAdminResponse = z.infer<typeof inviteAdminResponse>;
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequest>;
export type ContactStartRequest = z.infer<typeof contactStartRequest>;
export type ContactStartResponse = z.infer<typeof contactStartResponse>;
export type ContactConfirmRequest = z.infer<typeof contactConfirmRequest>;
